// src/components/App.js
import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { initializeStorageUtils } from '../utils/storageUtils';
import { initializeHistoryUtils } from '../utils/historyUtils';
import UserService from '../services/UserService';

// Component imports
import EnhancedPreferencesForm from './EnhancedPreferencesForm';
import WorkoutDisplay from './WorkoutDisplay';
import ProBanner from './ProBanner';
import WorkoutPrograms from './WorkoutPrograms';
import EnhancedProgressTracker from './EnhancedProgressTracker';
import UserProfiles from './UserProfiles';
import ExerciseBrowser from './ExerciseBrowser';
import FavoriteExercises from './FavoriteExercises';
import ImprovedNavigation from './ImprovedNavigation';
import WelcomeOverview from './WelcomeOverview';
import AuthModal from './auth/AuthModal';
import UserMenu from './auth/UserMenu';
import AccountSettings from './auth/AccountSettings';
import AppFooter from './AppFooter';
import SuccessHandler from './checkout/SuccessHandler';

function App() {
  const { 
    workout, 
    setWorkout, 
    savedWorkouts,
    setSavedWorkouts,
    currentView, 
    preferences,
    setPreferences,
    workoutHistory
  } = useAppContext();

  const { isAuthenticated, currentUser } = useAuth();
  
  const [showWelcome, setShowWelcome] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  
  // Initialize Firebase utilities
  useEffect(() => {
    // Initialize storage and history utils with current user getter
    const getCurrentUser = () => currentUser;
    initializeStorageUtils(getCurrentUser, UserService);
    initializeHistoryUtils(getCurrentUser);
  }, [currentUser]);
  
  // Check if this is the first time the user is visiting the app
  useEffect(() => {
    const isFirstVisit = localStorage.getItem('pilatesPulse_visited') !== 'true';
    setShowWelcome(isFirstVisit && workoutHistory.length === 0);
    
    // Mark as visited
    localStorage.setItem('pilatesPulse_visited', 'true');
  }, [workoutHistory.length]);
  
  const handleSelectProfile = (profile) => {
    // Update preferences from the selected profile
    setPreferences({
      ...preferences,
      ...profile,
    });
  };

  const handleShowAuthModal = (mode = 'login') => {
    setAuthModalMode(mode);
    setShowAuthModal(true);
  };

  const handleCloseAuthModal = () => {
    setShowAuthModal(false);
  };

  const handleShowAccountSettings = () => {
    setShowAccountSettings(true);
  };

  const handleCloseAccountSettings = () => {
    setShowAccountSettings(false);
  };
  
  // Render appropriate content based on current view
  const renderContent = () => {
    // Show welcome screen for first-time users
    if (showWelcome) {
      return <WelcomeOverview onClose={() => setShowWelcome(false)} />;
    }
    
    switch (currentView) {
      case 'generator':
        return !workout ? (
          <div className="space-y-6">
            {isAuthenticated && (
              <UserProfiles 
                onSelectProfile={handleSelectProfile} 
                currentPreferences={preferences}
              />
            )}
            <EnhancedPreferencesForm 
              setWorkout={setWorkout} 
              savedWorkouts={savedWorkouts}
              setSavedWorkouts={setSavedWorkouts}
              preferences={preferences}
              setPreferences={setPreferences}
            />
          </div>
        ) : (
          <WorkoutDisplay 
            workout={workout} 
            setWorkout={setWorkout}
            savedWorkouts={savedWorkouts}
            setSavedWorkouts={setSavedWorkouts}
          />
        );
        
      case 'programs':
        return <WorkoutPrograms />;
        
      case 'progress':
        return <EnhancedProgressTracker />;
        
      case 'exercises':
        return <ExerciseBrowser />;
        
      case 'favorites':
        return <FavoriteExercises />;
        
      default:
        return null;
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 text-gray-800">
      {/* Checkout success handler - invisible component that processes redirects */}
      <SuccessHandler />
      
      <header className="sticky top-0 z-40 bg-white shadow-sm backdrop-blur-sm bg-opacity-95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-2xl font-bold text-blue-600 flex items-center">
                  <span className="mr-2">🧘‍♀️</span>
                  PilatesPlanner
                </h1>
              </div>
              <p className="hidden md:block ml-4 text-sm text-gray-500">
                Personalized Pilates for every body
              </p>
            </div>
            <UserMenu 
              onShowAuthModal={handleShowAuthModal}
              onShowAccountSettings={handleShowAccountSettings}
            />
          </div>
        </div>
      </header>
      
      <div className="py-2 bg-blue-50 border-b border-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ImprovedNavigation />
        </div>
      </div>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Premium Banner at top of page - only show premium banner for authenticated users */}
        {isAuthenticated && <ProBanner />}
        
        {/* Show auth prompt for non-authenticated users */}
        {!isAuthenticated && currentView !== 'generator' && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-4 mb-6 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-4 md:mb-0">
                <h2 className="text-lg font-semibold text-blue-800 mb-1 flex items-center">
                  <span className="mr-2">✨</span>Get More from PilatesPlanner
                </h2>
                <p className="text-blue-700 text-sm">Create an account to save workouts, track progress, and more.</p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => handleShowAuthModal('signup')}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md shadow-sm transition-colors"
                >
                  Sign Up
                </button>
                <button
                  onClick={() => handleShowAuthModal('login')}
                  className="px-4 py-2 bg-white hover:bg-gray-50 text-blue-600 text-sm font-medium rounded-md border border-blue-200 shadow-sm transition-colors"
                >
                  Log In
                </button>
              </div>
            </div>
          </div>
        )}
        
        <div className="transition-opacity duration-300">
          {renderContent()}
        </div>
      </main>
      
      <AppFooter />

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={handleCloseAuthModal} 
        mode={authModalMode}
      />

      {/* Account Settings Modal */}
      <AccountSettings 
        isOpen={showAccountSettings} 
        onClose={handleCloseAccountSettings}
      />
    </div>
  );
}

export default App;